import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { AppointmentAttendee, SchedulingSubject } from '../../../core/interfaces/academic-appointment';
import { SubjectChoiceService } from '../../../core/services/utils/subject-choice.service';

/** Ce que le bloc lit d'une séance — l'agenda comme l'inspection le satisfont tels quels. */
export interface SessionAudience {
  subjects?: SchedulingSubject[] | null;
  subjectName?: string | null;
  groupName?: string | null;
  students?: AppointmentAttendee[] | null;
}

/**
 * Pour qui est la séance : le niveau, la matière, le groupe, les élèves —
 * chacun sous son libellé.
 *
 * <p>Les panneaux de détail les alignaient en lignes à icône seule : un titre
 * de parcours bilingue suivi d'un nom de groupe qui le répète ne se lisait
 * plus, on ne savait pas où finissait le niveau et où commençait le groupe.
 * Les trois agendas (administration, professeur, inspection) partagent ce bloc
 * pour dire la même chose de la même façon.</p>
 */
@Component({
  selector: 'app-session-audience',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './session-audience.component.html',
  styleUrl: './session-audience.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionAudienceComponent {
  private readonly subjectChoice = inject(SubjectChoiceService);

  private readonly translate = inject(TranslateService);

  readonly session = input.required<SessionAudience>();

  // Les libellés se choisissent selon la langue courante, que le service lit
  // hors signal : sans cette dépendance, le bloc gardait l'ancienne langue
  // après une bascule.
  private readonly lang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly level = computed(() => {
    this.lang();
    return this.subjectChoice.levelsOf(this.session().subjects);
  });
  readonly subject = computed(() => {
    this.lang();
    return this.subjectChoice.subjectsOf(this.session());
  });
  readonly students = computed(() => this.session().students ?? []);
}
